import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { usePageTracking } from "@/hooks/useAnalytics";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/data/queryClient";
import { GlobalErrorBoundary } from "@/components/GlobalErrorBoundary";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ScrollToTop } from "./components/ScrollToTop";
import { useNativeAuth } from "@/hooks/useNativeAuth";
import { lazyWithRetry } from "@/lib/lazyWithRetry";
import { CheckinLikesProvider } from "@/contexts/CheckinLikesContext";
import { FollowProvider } from "@/contexts/FollowContext";

// Critical path — keep eagerly loaded
import Splash from "./pages/Splash";
import NotFound from "./pages/NotFound";
import ProtectedRoute from "./components/ProtectedRoute";
import AppLayout from "./components/AppLayout";

// Lazy-loaded pages (with automatic retry + reload on stale chunks)

const Index = lazyWithRetry(() => import("./pages/Index"), "Index");
const Auth = lazyWithRetry(() => import("./pages/Auth"), "Auth");
const AddBurger = lazyWithRetry(() => import("./pages/AddBurger"), "AddBurger");
const Profile = lazyWithRetry(() => import("./pages/Profile"), "Profile");
const Discover = lazyWithRetry(() => import("./pages/Discover"), "Discover");
const MapView = lazyWithRetry(() => import("./pages/MapView"), "MapView");
const BurgerDetail = lazyWithRetry(() => import("./pages/BurgerDetail"), "BurgerDetail");
const ProducerDetail = lazyWithRetry(() => import("./pages/ProducerDetail"), "ProducerDetail");
const UserProfile = lazyWithRetry(() => import("./pages/UserProfile"), "UserProfile");
const EmailPreview = lazyWithRetry(() => import("./pages/EmailPreview"), "EmailPreview");
const VanityProfile = lazyWithRetry(() => import("./pages/VanityProfile"), "VanityProfile");
const Friends = lazyWithRetry(() => import("./pages/Friends"), "Friends");
const Terms = lazyWithRetry(() => import("./pages/Terms"), "Terms");
const Privacy = lazyWithRetry(() => import("./pages/Privacy"), "Privacy");
const Leaderboard = lazyWithRetry(() => import("./pages/Leaderboard"), "Leaderboard");
const LeaderboardsDirectory = lazyWithRetry(() => import("./pages/LeaderboardsDirectory"), "LeaderboardsDirectory");
const AccountDeletion = lazyWithRetry(() => import("./pages/AccountDeletion"), "AccountDeletion");
const CheckinsJournal = lazyWithRetry(() => import("./pages/CheckinsJournal"), "CheckinsJournal");
const Download = lazyWithRetry(() => import("./pages/Download"), "Download");
const Feedback = lazyWithRetry(() => import("./pages/Feedback"), "Feedback");
const ClaimBadge = lazyWithRetry(() => import("./pages/ClaimBadge"), "ClaimBadge");
const ExpertReviewsAdmin = lazyWithRetry(() => import("./pages/ExpertReviewsAdmin"), "ExpertReviewsAdmin");
const SeedAdmin = lazyWithRetry(() => import("./pages/SeedAdmin"), "SeedAdmin");
const AdminHome = lazyWithRetry(() => import("./pages/AdminHome"), "AdminHome");
const AdminLocations = lazyWithRetry(() => import("./pages/AdminLocations"), "AdminLocations");
const AdminRoute = lazyWithRetry(() => import("./components/AdminRoute"), "AdminRoute");





function AppWithTracking() {
  usePageTracking();
  useNativeAuth(); // Handle OAuth deep links in native apps
  
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-primary" />
      </div>
    }>
    <CheckinLikesProvider>
    <FollowProvider>
    <Routes>
      <Route path="/" element={<Splash />} />
      <Route path="/auth" element={<Splash />} /> {/* Keep /auth as alias for SEO & existing links */}
      <Route path="/signin" element={<Splash authMode="signIn" />} />
      <Route path="/signup" element={<Splash authMode="signUp" />} />
      <Route path="/u/:username" element={<VanityProfile />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/download" element={<Download />} />
      <Route path="/feedback" element={<Feedback />} />
      <Route path="/email-preview" element={<ProtectedRoute><EmailPreview /></ProtectedRoute>} />
      <Route path="/account-deletion" element={<AccountDeletion />} />
      <Route path="/claim/founding-reviewer" element={<ClaimBadge />} />
      
      {/* PUBLIC ROUTES - Accessible without login for SEO */}
      <Route element={<AppLayout />}>
        <Route path="/leaderboards" element={<LeaderboardsDirectory />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/leaderboard/:location" element={<Leaderboard />} />
        <Route path="/burger/:slug" element={<BurgerDetail />} />
        <Route path="/producer/:slug" element={<ProducerDetail />} />
        {/* Legacy UUID routes - redirect to new slug-based routes */}
        <Route path="/burgers/:id" element={<BurgerDetail />} />
        <Route path="/producers/:id" element={<ProducerDetail />} />
        {/* Public map - works for guests too (maps-key allows anonymous with rate limits) */}
        <Route path="/map" element={<MapView />} />
      </Route>
      
      {/* PROTECTED ROUTES - Require authentication */}
      <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
        <Route path="/home" element={<Index />} />
        <Route path="/discover" element={<Discover />} />
        <Route path="/activity" element={<Index />} />
        <Route path="/add" element={<AddBurger />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/friends" element={<Friends />} />
        <Route path="/follow" element={<Friends />} />
        <Route path="/users/:id" element={<UserProfile />} />
        <Route path="/users/:userId/checkins" element={<CheckinsJournal />} />
        <Route path="/checkins" element={<CheckinsJournal />} />
        <Route path="/journal" element={<CheckinsJournal />} />
        <Route element={<AdminRoute />}>
          <Route path="/admin" element={<AdminHome />} />
          <Route path="/admin/locations" element={<AdminLocations />} />
          <Route path="/admin/expert-reviews" element={<ExpertReviewsAdmin />} />
          <Route path="/admin/seed" element={<SeedAdmin />} />
        </Route>


      </Route>
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </FollowProvider>
    </CheckinLikesProvider>
    </Suspense>
  );
}

const App = () => (
  <GlobalErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />
          <AppWithTracking />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </GlobalErrorBoundary>
);

export default App;
